import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import {
  useAddFollowUpEvent,
  useAssignFollowUp,
  useFollowUp,
  useFollowUpAssignees,
  useFollowUpScript,
} from '@/hooks/useFollowUps'
import { useSendWAMessage } from '@/hooks/useWhatsApp'
import type { ApiError, FollowUpAddEventType, FollowUpRow } from '@/lib/api-client'
import { cn, formatCurrency } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { Ban, Copy, ExternalLink, Loader2, MessageCircle } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'
import {
  MANUAL_OUTCOME_OPTIONS,
  MANUAL_TRACKING_OPTIONS,
  QUEUE_META,
  eventLabel,
  isAutoSource,
  sourceLabel,
} from './queue-meta'

/** 'YYYY-MM-DD' in Asia/Kuala_Lumpur for either a date-only or ISO datetime string. */
function mytDay(date: string): string {
  if (date.length === 10) return date
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  }).format(new Date(date))
}

function formatDayHeader(day: string): string {
  return new Date(`${day}T00:00:00+08:00`).toLocaleDateString('en-MY', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kuala_Lumpur',
  })
}

interface FollowUpDrawerProps {
  row: FollowUpRow | null
  branchId: number | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function FollowUpDrawer({ row, branchId, open, onOpenChange }: FollowUpDrawerProps) {
  const { data: detailRes, isPending: detailPending } = useFollowUp(row?.id ?? null)
  const { data: scriptRes, isPending: scriptPending } = useFollowUpScript(row?.id ?? null)
  const { data: assigneesRes } = useFollowUpAssignees(branchId)
  const assignMutation = useAssignFollowUp()
  const addEventMutation = useAddFollowUpEvent()
  const sendMessage = useSendWAMessage()

  const [messageText, setMessageText] = React.useState('')
  const [note, setNote] = React.useState('')
  const [selectedOutcome, setSelectedOutcome] = React.useState<FollowUpAddEventType | null>(null)
  const [waError, setWaError] = React.useState<string | null>(null)
  const [blacklistOpen, setBlacklistOpen] = React.useState(false)
  const [blacklistReason, setBlacklistReason] = React.useState('')

  React.useEffect(() => {
    setMessageText(scriptRes?.text ?? '')
    setWaError(null)
  }, [scriptRes?.text, row?.id])

  React.useEffect(() => {
    setSelectedOutcome(null)
    setNote('')
    setBlacklistOpen(false)
    setBlacklistReason('')
  }, [row?.id])

  if (!row) return null

  const detail = detailRes
  const queueMeta = QUEUE_META[row.queue]
  // Prefer the fresh detail row (it refetches after the blacklist mutation);
  // fall back to the list row the drawer was opened with.
  const blacklistedAt = detail?.patient.blacklistedAt ?? row.patient.blacklistedAt
  const isBlacklisted = blacklistedAt !== null && blacklistedAt !== undefined

  const handleBlacklist = async () => {
    const reason = blacklistReason.trim()
    if (!reason) return
    await addEventMutation.mutateAsync({ id: row.id, data: { type: 'blacklisted', note: reason } })
    setBlacklistOpen(false)
    setBlacklistReason('')
    toast.success('Patient blacklisted — removed from all follow-ups and campaigns')
  }

  const handleSend = async () => {
    if (!row.wa?.conversationId) return
    setWaError(null)
    try {
      await sendMessage.mutateAsync({
        conversationId: row.wa.conversationId,
        data: { type: 'text', text: messageText },
      })
      toast.success('Message sent from Nurin inbox')
    } catch (err) {
      const apiErr = err as ApiError
      if (apiErr.code === 'WINDOW_EXPIRED') {
        setWaError('24h window closed — open the chat and send a template instead.')
      } else {
        setWaError(apiErr.message || 'Failed to send message')
      }
    }
  }

  const handleOutcomeSubmit = async () => {
    if (!selectedOutcome) return
    await addEventMutation.mutateAsync({
      id: row.id,
      data: { type: selectedOutcome, note: note.trim() || undefined },
    })
    setSelectedOutcome(null)
    setNote('')
  }

  // wa.me redirect flow (CA's own phone) — used whenever the Nurin inbox
  // can't send free text (no 24h window, or no WA contact at all). Opens
  // the deep link with the suggested message prefilled and logs a
  // `wa_opened` event (best-effort — never blocks the redirect on failure).
  const handleOpenInWhatsApp = () => {
    if (!row.waLink) return
    window.open(`${row.waLink}?text=${encodeURIComponent(messageText)}`, '_blank')
    addEventMutation.mutate({ id: row.id, data: { type: 'wa_opened' } })
  }

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(messageText)
      toast.success('Copied')
    } catch {
      toast.error('Failed to copy message')
    }
  }

  const waWindowOpen = row.wa?.waWindowOpen ?? false
  const canRedirect = row.waLink !== null

  const timeline = detail
    ? [...detail.events]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map((ev) => ({
          key: `event-${ev.id}`,
          date: ev.createdAt,
          label: eventLabel(ev.type, ev.source),
          note: ev.note,
          auto: isAutoSource(ev.source),
          source: sourceLabel(ev.source),
        }))
    : []

  // Avixo books one appointment per procedure, so a patient often has several
  // slots on one day and staff only flip the first to "attended". A day counts
  // as attended if any slot was attended or any invoice exists; on such days
  // the leftover per-slot no-shows are noise and are hidden.
  const attendedDays = new Set<string>()
  for (const v of detail?.visits ?? []) {
    if (v.kind === 'invoice' || v.label === 'Appointment attended') attendedDays.add(mytDay(v.date))
  }

  const visitTimeline = detail
    ? detail.visits
        .filter(
          (v) =>
            !(
              v.kind === 'appointment' &&
              v.label !== 'Appointment attended' &&
              attendedDays.has(mytDay(v.date))
            )
        )
        .map((v, i) => ({
          key: `visit-${i}`,
          date: v.date,
          label: v.label,
          note:
            [
              v.doctor,
              v.kind === 'invoice' && v.amount !== null
                ? v.amount > 0
                  ? formatCurrency(v.amount)
                  : 'RM 0 · redeem / FOC'
                : null,
            ]
              .filter(Boolean)
              .join(' · ') || null,
          auto: true,
          source: 'AVIXO',
        }))
    : []

  const merged = [...timeline, ...visitTimeline].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  // Group by MYT day, latest day first (merged is already sorted desc).
  const dayGroups: { day: string; items: typeof merged }[] = []
  for (const item of merged) {
    const day = mytDay(item.date)
    const last = dayGroups[dayGroups.length - 1]
    if (last && last.day === day) last.items.push(item)
    else dayGroups.push({ day, items: [item] })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{row.patient.name}</SheetTitle>
          <SheetDescription className="font-mono text-xs">
            {row.patient.pno} · {row.patient.phone ?? 'no phone'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 px-4 pb-6 sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold',
                queueMeta.badgeClassName
              )}
            >
              {queueMeta.label}
            </span>
            {isBlacklisted && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-2.5 py-1 text-[12px] font-bold text-destructive">
                <Ban className="h-3.5 w-3.5" /> Blacklisted
              </span>
            )}
          </div>

          {isBlacklisted && (
            <div className="flex items-start gap-2 rounded-xl bg-destructive/10 p-3 text-[12.5px] leading-relaxed text-destructive">
              <Ban className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-bold">Do not contact this patient</div>
                Blacklisted on{' '}
                {new Date(blacklistedAt as string).toLocaleDateString('en-MY', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  timeZone: 'Asia/Kuala_Lumpur',
                })}
                . Excluded from every follow-up queue and WhatsApp campaign. An admin can remove
                this from the{' '}
                <Link
                  to="/patients/$id"
                  params={{ id: String(row.patientId) }}
                  className="font-semibold underline"
                >
                  patient profile
                </Link>
                .
              </div>
            </div>
          )}

          {detailPending ? (
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading follow-up...
            </div>
          ) : (
            <>
              {/* Key/value cards */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="rounded-xl bg-muted p-3">
                  <div className="mb-1 text-[10.5px] font-bold uppercase tracking-wide text-text-muted">
                    Package
                  </div>
                  {detail?.packages[0] ? (
                    <>
                      <div className="text-sm font-semibold">{detail.packages[0].name}</div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-border">
                        <div
                          className="h-full rounded-full bg-info"
                          style={{
                            width: `${Math.min(100, (detail.packages[0].used / Math.max(1, detail.packages[0].bought)) * 100)}%`,
                          }}
                        />
                      </div>
                      <div className="mt-1 text-[11px] text-text-muted">
                        {detail.packages[0].used} / {detail.packages[0].bought} sessions used
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-text-muted">
                      {row.triggerRef.packageName ?? 'No active package'}
                    </div>
                  )}
                </div>
                <div className="rounded-xl bg-muted p-3">
                  <div className="mb-1 text-[10.5px] font-bold uppercase tracking-wide text-text-muted">
                    {row.queue === 'consult_no_purchase' ? 'Consult fee paid' : 'Value'}
                  </div>
                  <div className="text-[15px] font-bold">
                    {row.queue === 'no_show' ? (
                      <span className="text-text-light">—</span>
                    ) : (
                      formatCurrency(row.value)
                    )}
                  </div>
                </div>
                <div className="rounded-xl bg-muted p-3">
                  <div className="mb-1 text-[10.5px] font-bold uppercase tracking-wide text-text-muted">
                    Doctor
                  </div>
                  <div className="text-sm font-semibold">{row.triggerRef.doctor ?? '-'}</div>
                </div>
                <div className="rounded-xl bg-muted p-3">
                  <div className="mb-1 text-[10.5px] font-bold uppercase tracking-wide text-text-muted">
                    CA in charge
                  </div>
                  <Select
                    value={row.assignedTo ? String(row.assignedTo) : 'unassigned'}
                    onValueChange={(val) =>
                      assignMutation.mutate({
                        id: row.id,
                        assignedTo: val === 'unassigned' ? null : Number(val),
                      })
                    }
                  >
                    <SelectTrigger className="h-8 border-0 bg-transparent p-0 text-sm font-semibold shadow-none">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {assigneesRes?.data.map((a) => (
                        <SelectItem key={a.id} value={String(a.id)}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Suggested message */}
              <div className={cn(isBlacklisted && 'pointer-events-none opacity-40')}>
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-text-muted">
                    Suggested message{' '}
                    {scriptRes?.templateKey && (
                      <span className="font-mono normal-case tracking-normal text-text-light">
                        · {scriptRes.templateKey}
                      </span>
                    )}
                  </p>
                </div>
                {!waWindowOpen && (
                  <div className="mb-2 flex items-start gap-2 rounded-xl bg-info/10 p-3 text-[12.5px] leading-relaxed text-info">
                    <span aria-hidden>ⓘ</span>
                    <div>
                      <div className="font-bold">
                        Nurin inbox can&apos;t send free text to this patient
                      </div>
                      No reply in the last 24 hours. Send from your phone, then mark it as sent.
                    </div>
                  </div>
                )}
                {waWindowOpen && (
                  <div className="mb-2 flex items-start gap-2 rounded-xl bg-success/10 p-3 text-[12.5px] leading-relaxed text-success">
                    <span aria-hidden>✓</span>
                    <div>
                      <div className="font-bold">24-hour window is open</div>
                      This patient replied recently — you can send straight from the Nurin inbox.
                    </div>
                  </div>
                )}
                <Textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  disabled={scriptPending}
                  rows={6}
                  className="text-sm"
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {waWindowOpen ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        className="flex-1 gap-2 bg-[#25d366] text-[#062b13] hover:bg-[#25d366]/90"
                        disabled={sendMessage.isPending || !messageText.trim()}
                        onClick={handleSend}
                      >
                        {sendMessage.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <MessageCircle className="h-4 w-4" />
                        )}
                        Send from Nurin inbox
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="gap-2"
                        disabled={!canRedirect}
                        onClick={handleOpenInWhatsApp}
                      >
                        Open in WhatsApp instead
                      </Button>
                    </>
                  ) : canRedirect ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        className="flex-1 gap-2 bg-[#25d366] text-[#062b13] hover:bg-[#25d366]/90"
                        disabled={!messageText.trim()}
                        onClick={handleOpenInWhatsApp}
                      >
                        <MessageCircle className="h-4 w-4" />
                        Open in WhatsApp
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="gap-2"
                        onClick={handleCopyMessage}
                      >
                        <Copy className="h-4 w-4" />
                        Copy message
                      </Button>
                    </>
                  ) : (
                    <p className="text-xs text-text-muted">No phone number on file.</p>
                  )}
                  {row.wa && (
                    <Link
                      to="/whatsapp"
                      search={{ id: row.wa.conversationId }}
                      className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-hover"
                    >
                      Open chat <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </div>
                {!waWindowOpen && canRedirect && (
                  <p className="mt-2 text-[11.5px] text-text-muted">
                    Opens <code className="font-mono">{row.waLink}</code> with the message
                    prefilled. Clicking auto-marks &ldquo;Opened in WhatsApp&rdquo; below — confirm
                    with &ldquo;Sent via WhatsApp&rdquo; once you&apos;ve actually sent it.
                  </p>
                )}
                {waError && (
                  <p className="mt-2 text-xs text-destructive">
                    {waError}{' '}
                    {row.wa && (
                      <Link
                        to="/whatsapp"
                        search={{ id: row.wa.conversationId }}
                        className="underline"
                      >
                        Open chat
                      </Link>
                    )}
                  </p>
                )}
              </div>

              {/* Timeline */}
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-text-muted">
                  Timeline
                </p>
                {dayGroups.length === 0 && (
                  <div className="text-sm text-text-muted">No activity yet.</div>
                )}
                <div className="space-y-4">
                  {dayGroups.map((group) => (
                    <div key={group.day}>
                      <div className="mb-1.5 flex items-center gap-2">
                        <span className="text-[12px] font-bold">{formatDayHeader(group.day)}</span>
                        <span className="h-px flex-1 bg-border" />
                      </div>
                      <ul className="space-y-2">
                        {group.items.map((item) => (
                          <li
                            key={item.key}
                            className="grid grid-cols-[14px_1fr] gap-2.5 text-[12.5px]"
                          >
                            <span
                              className={cn(
                                'mt-1 h-2 w-2 justify-self-center rounded-full',
                                item.auto ? 'bg-success' : 'bg-text-light'
                              )}
                            />
                            <div>
                              <div className="font-medium">{item.label}</div>
                              {item.note && (
                                <div className="text-text-muted">&ldquo;{item.note}&rdquo;</div>
                              )}
                              <div className="text-[11px] text-text-muted">
                                {item.date.length > 10 && (
                                  <>
                                    {new Date(item.date).toLocaleTimeString('en-MY', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZone: 'Asia/Kuala_Lumpur',
                                    })}{' '}
                                    ·{' '}
                                  </>
                                )}
                                {item.source}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* After contacting / outcome */}
              <div className={cn(isBlacklisted && 'pointer-events-none opacity-40')}>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-text-muted">
                  After contacting, mark as
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {MANUAL_TRACKING_OPTIONS.map((opt) => (
                    <button
                      key={opt.type}
                      type="button"
                      onClick={() => setSelectedOutcome(opt.type)}
                      className={cn(
                        'rounded-lg border px-2.5 py-2 text-[12.5px] font-semibold',
                        selectedOutcome === opt.type
                          ? 'border-text-dark bg-active'
                          : 'border-border bg-card hover:bg-hover'
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <p className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-wide text-text-muted">
                  Or record an outcome
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {MANUAL_OUTCOME_OPTIONS.filter((opt) => opt.type !== 'not_interested').map(
                    (opt) => (
                      <button
                        key={opt.type}
                        type="button"
                        onClick={() => setSelectedOutcome(opt.type)}
                        className={cn(
                          'rounded-lg border px-2.5 py-2 text-[12.5px] font-semibold',
                          selectedOutcome === opt.type
                            ? 'border-text-dark bg-active'
                            : 'border-border bg-card hover:bg-hover'
                        )}
                      >
                        {opt.label}
                      </button>
                    )
                  )}
                </div>

                <div className="mt-1.5">
                  <button
                    type="button"
                    onClick={() => setSelectedOutcome('not_interested')}
                    className={cn(
                      'w-full rounded-lg border px-2.5 py-2 text-[12.5px] font-semibold text-destructive',
                      selectedOutcome === 'not_interested'
                        ? 'border-destructive bg-destructive/10'
                        : 'border-border bg-card hover:bg-hover'
                    )}
                  >
                    Not interested
                  </button>
                  <p className="mt-1 text-[11px] text-text-muted">
                    Snoozes this patient for 90 days (365 days if this happened before).
                  </p>
                </div>

                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Short note (optional)"
                  rows={2}
                  className="mt-2 text-sm"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2 w-full"
                  disabled={!selectedOutcome || addEventMutation.isPending}
                  onClick={handleOutcomeSubmit}
                >
                  {addEventMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save
                </Button>
                <p className="mt-2 text-[11.5px] text-text-muted">
                  &ldquo;Booked&rdquo; and &ldquo;Repurchased&rdquo; are detected automatically from
                  Avixo sync. CAs don&apos;t need to tick these.
                </p>

                {!isBlacklisted && (
                  <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-dashed border-border px-3 py-2">
                    <div className="text-[11.5px] leading-snug text-text-muted">
                      <span className="font-semibold text-text-dark">Blacklist patient</span> —
                      permanent. Removes them from every follow-up and WhatsApp campaign.
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="shrink-0 gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => setBlacklistOpen(true)}
                    >
                      <Ban className="h-3.5 w-3.5" /> Blacklist
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <AlertDialog open={blacklistOpen} onOpenChange={setBlacklistOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Blacklist {row.patient.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                The patient will be removed from this and every future follow-up queue and from all
                WhatsApp campaign audiences. Only an admin can undo this from the patient profile.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div>
              <label
                htmlFor="blacklist-reason"
                className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-text-muted"
              >
                Reason (required)
              </label>
              <Textarea
                id="blacklist-reason"
                value={blacklistReason}
                onChange={(e) => setBlacklistReason(e.target.value)}
                placeholder="e.g. Patient asked not to be contacted again"
                rows={3}
                className="text-sm"
                autoFocus
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={addEventMutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-white hover:bg-destructive/90"
                disabled={!blacklistReason.trim() || addEventMutation.isPending}
                onClick={(e) => {
                  e.preventDefault()
                  void handleBlacklist()
                }}
              >
                {addEventMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Blacklist patient
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  )
}
